import {type FC, useEffect, useState} from 'react';
import {JaaSMeeting} from '@jitsi/react-sdk';
import useVideoCallStore from '../../../store/useVideoCallStore.ts';
import {ControlButton, ControlPanel, ExitButton, VideoCallScreenWrapper} from '../types/VideoCallScreen.styled.ts';
import type {IJitsiMeetExternalApi} from '@jitsi/react-sdk/lib/types';
import useCenterStore from '../../../store/useCenterStore.ts';
import {FocusContext, setFocus, useFocusable} from '@noriginmedia/norigin-spatial-navigation';

const VideoCallScreen: FC = () => {
    const {setIsCalling, roomName, videoCallToken} = useVideoCallStore();
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoMuted, setIsVideoMuted] = useState(false);
    const [api, setApi] = useState<IJitsiMeetExternalApi | null>(null);

    const handleJaaSIFrameRef = (iframeRef: HTMLDivElement) => {
        iframeRef.style.border = '0px solid #3d3d3d';
        iframeRef.style.background = '#3d3d3d';
        iframeRef.style.width = '100%';
        iframeRef.style.aspectRatio = '19/9';
    };

    const handleApiReady = (apiObj: IJitsiMeetExternalApi) => {
        setApi(apiObj);
        //@ts-ignore
        apiObj.on('audioMuteStatusChanged', (event: any) => {
            setIsMuted(event.muted);
        });
        //@ts-ignore
        apiObj.on('videoMuteStatusChanged', (event: any) => {
            setIsVideoMuted(event.muted);
        });
    };

    const handleExitCall = () => {
        if (api) {
            api.executeCommand('hangup');
        }
        setIsCalling(false);
        setFocus('video-calling');
    };

    const handleToggleMic = () => {
        if (api) {
            api.executeCommand('toggleAudio');
        }
    };

    const handleToggleVideo = () => {
        if (api) {
            api.executeCommand('toggleVideo');
        }
    };

    const configOverwrite = {
        notifications: [],
        toolbarButtons: [],
        filmStripOnly: true,
        startWithAudioMuted: false,
        startWithVideoMuted: false,
        prejoinPageEnabled: false,
        enableWelcomePage: false,
        prejoinConfig: {
            enabled: false,
            hideDisplayName: true,
            hideExtraJoinButtons: true,
        },
    };

    const {videoInputD, audioInputD} = useCenterStore();

    const updateDevices = async () => {
        if (api && videoInputD && videoInputD.deviceId) {
            await api!.setVideoInputDevice(videoInputD.label, videoInputD.deviceId);
        }
        if (api && audioInputD && audioInputD.deviceId) {
            await api.setAudioInputDevice(audioInputD.label, audioInputD.deviceId);
        }
    };

    useEffect(() => {
        updateDevices();
    }, [videoInputD, audioInputD]);

    const {ref: videoCallScreenRef, focusKey: videoCallScreenFocusKey} = useFocusable({
        focusKey: 'video-call-screen',
        preferredChildFocusKey: 'video-call-mute',
        isFocusBoundary: true,
    });

    const {ref: videoCallMuteRef, focused: videoCallMuteFocused} = useFocusable({
        focusKey: `video-call-mute`,
        onEnterPress: handleToggleMic,
        onArrowPress: (direction) => {
            if (direction === 'right') {
                setFocus('video-call-camera');
            }
            return false;
        },
    });

    const {ref: videoCallCameraRef, focused: videoCallCameraFocused} = useFocusable({
        focusKey: `video-call-camera`,
        onEnterPress: handleToggleVideo,
        onArrowPress: (direction) => {
            if (direction === 'left') {
                setFocus('video-call-mute');
            } else if (direction === 'right') {
                setFocus('video-call-hang-up');
            }
            return false;
        },
    });

    const {ref: videoCallHangupRef, focused: videoCallHangupFocused} = useFocusable({
        focusKey: `video-call-hang-up`,
        onEnterPress: handleExitCall,
        onArrowPress: (direction) => {
            if (direction === 'left') {
                setFocus('video-call-camera');
            }
            return false;
        },
    });

    return (
        <VideoCallScreenWrapper>
            <JaaSMeeting
                appId={import.meta.env.VITE_ICSC_APP_ID}
                jwt={videoCallToken}
                roomName={roomName}
                configOverwrite={configOverwrite}
                getIFrameRef={handleJaaSIFrameRef}
                onApiReady={handleApiReady}
            />
            <FocusContext.Provider value={videoCallScreenFocusKey}>
                <ControlPanel ref={videoCallScreenRef}>
                    <ControlButton
                        ref={videoCallMuteRef}
                        $focused={videoCallMuteFocused}
                        onClick={handleToggleMic}
                        isActive={isMuted}
                    >
                        {isMuted ? '🔇 음소거 해제' : '🎤 음소거'}
                    </ControlButton>
                    <ControlButton
                        ref={videoCallCameraRef}
                        $focused={videoCallCameraFocused}
                        onClick={handleToggleVideo}
                        isActive={isVideoMuted}
                    >
                        {isVideoMuted ? '📹 카메라 켜기' : '📷 카메라 끄기'}
                    </ControlButton>
                    <ExitButton ref={videoCallHangupRef} $focused={videoCallHangupFocused} onClick={handleExitCall}>
                        📞 통화 종료
                    </ExitButton>
                </ControlPanel>
            </FocusContext.Provider>
        </VideoCallScreenWrapper>
    );
};

export default VideoCallScreen;
